import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import axios from "axios";

import {
  Users,
  ChevronLeft,
  ChevronRight,
  Paperplane,
} from "../../files/icons/index.js";
import { Button, Status } from "../components/component.js";
import Helmet from "../components/helmet.js";
import {
  DateToFormat,
  parseDateYMD,
} from "../../utils/datetime.js";
import ClaimFormModal from "../components/claimFormModal.js";
import BatchClaimReviewModal from "../components/batchClaimReviewModal.js";
import PaidModal from "../components/paidModal.js";

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return DateToFormat(d, "YYYY-MM");
}

function Claims() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [activeClaim, setActiveClaim] = useState(null);
  const [viewClaim, setViewClaim] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(null);
  const [orgSettings, setOrgSettings] = useState(null);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchClaims, setBatchClaims] = useState(null);
  const [paidClaim, setPaidClaim] = useState(null);
  const [savingPaid, setSavingPaid] = useState(false);
  const [paidError, setPaidError] = useState(null);

  const [month, setMonth] = useState(() => DateToFormat(new Date(), "YYYY-MM"));

  const viewClaimRef = useRef(null);
  viewClaimRef.current = viewClaim;

  useEffect(() => {
    axios.get("/api/admin/settings").then(({ data }) => setOrgSettings(data));
  }, []);

  const refreshClaimDetail = useCallback((patientID) => {
    axios
      .post("/api/admin/claims/detail", { patientID, period: month })
      .then(({ data }) => {
        setViewClaim((prev) => {
          if (!prev || prev.id !== patientID) return prev;
          return {
            ...prev,
            claimStatus: data.claimStatus,
            controlNumber: data.controlNumber,
            details: data.details,
            form: data.form,
            acknowledgment: data.acknowledgment,
          };
        });
      })
      .catch(() => {});
  }, [month]);

  function fetchClaims() {
    return axios
      .post("/api/admin/claims/list", { month })
      .then(({ data }) => setClaims(data.claims || []))
      .catch(() => setClaims([]));
  }

  useEffect(() => {
    setClaims(null);
    setSelectedIds(new Set());
    setBatchClaims(null);
    setPaidClaim(null);
    fetchClaims();
  }, [month]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const source = new EventSource(
      `/api/admin/claims/stream?month=${encodeURIComponent(month)}&token=${encodeURIComponent(token)}`
    );

    source.addEventListener("claim_status", (event) => {
      let update;
      try {
        update = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!update?.patientID) return;

      setClaims((prev) => {
        if (!prev) return prev;
        return prev.map((claim) =>
          claim.id === update.patientID
            ? {
                ...claim,
                claimStatus: update.claimStatus ?? claim.claimStatus,
                controlNumber: update.controlNumber ?? claim.controlNumber,
                details: update.details ?? claim.details,
              }
            : claim
        );
      });

      const open = viewClaimRef.current;
      if (open?.id === update.patientID) {
        refreshClaimDetail(update.patientID);
      }
    });

    source.onerror = () => {
      // EventSource reconnects automatically; debounce a list refresh for missed events.
      if (source.readyState === EventSource.CLOSED) {
        fetchClaims();
      }
    };

    return () => {
      source.close();
    };
  }, [month, refreshClaimDetail]);

  function updatePeriod(direction) {
    setMonth((prev) => shiftMonth(prev, direction));
  }

  function isReady(claim) {
    return !!claim.monthlyCheckinComplete;
  }

  function canResubmit(claim) {
    return claim.claimStatus === "rejected";
  }

  function hasSubmittedClaim(claim) {
    return (
      claim.claimStatus === "submitted" ||
      claim.claimStatus === "accepted" ||
      claim.claimStatus === "received" ||
      claim.claimStatus === "rejected" ||
      claim.claimStatus === "pending" ||
      claim.claimStatus === "finalized"
    );
  }

  function isSelectable(claim) {
    return isReady(claim) && !hasSubmittedClaim(claim);
  }

  function canMarkPaid(claim) {
    return hasSubmittedClaim(claim) && claim.claimStatus !== "rejected";
  }

  const selectableIds = useMemo(() => {
    if (!claims) return [];
    return claims.filter(isSelectable).map((c) => c.id);
  }, [claims]);

  useEffect(() => {
    if (!claims) return;
    const selectable = new Set(claims.filter(isSelectable).map((c) => c.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (selectable.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [claims]);

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const allSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(selectableIds);
    });
  }

  const allSelectableSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));

  function claimDetails(claim) {
    if (claim.claimStatus === "submitted") {
      return claim.controlNumber ? `Submitted · ${claim.controlNumber}` : "Submitted";
    }
    if (claim.claimStatus === "accepted") {
      return claim.controlNumber ? `Accepted · ${claim.controlNumber}` : "Accepted";
    }
    if (claim.claimStatus === "received") {
      return claim.controlNumber ? `Received · ${claim.controlNumber}` : "Received";
    }
    if (claim.claimStatus === "rejected") {
      return claim.details || "Rejected";
    }
    if (claim.claimStatus === "pending") {
      return claim.controlNumber ? `Pending · ${claim.controlNumber}` : "Pending";
    }
    if (claim.claimStatus === "finalized") {
      return claim.controlNumber ? `Finalized · ${claim.controlNumber}` : "Finalized";
    }
    if (!isReady(claim)) {
      return "Missing monthly check-in";
    }
    return claim.monthlyCheckinDate ? `Checked in · ${claim.monthlyCheckinDate}${claim.monthlyCheckinByName ? ` by ${claim.monthlyCheckinByName}` : ""}` : "";
  }

  function statusBadge(claim) {
    if (claim.claimStatus === "accepted") {
      return <Status color="green" text="Accepted" size="small" muted />;
    }
    if (claim.claimStatus === "received") {
      return <Status color="blue" text="Received" size="small" muted />;
    }
    if (claim.claimStatus === "rejected") {
      return <Status color="red" text="Rejected" size="small" muted />;
    }
    if (claim.claimStatus === "submitted") {
      return <Status color="blue" text="Submitted" size="small" muted />;
    }
    if (claim.claimStatus === "pending") {
      return <Status color="orange" text="Pending" size="small" muted />;
    }
    if (claim.claimStatus === "finalized") {
      return <Status color="grey" text="Finalized" size="small" muted />;
    }
    if (isReady(claim)) {
      return <Status color="purple" text="Ready" size="small" muted />;
    }
    return <span className="typo-normal-150 text-muted">Not Ready</span>;
  }

  function openClaimModal(claim) {
    if (!isReady(claim)) return;
    setBatchClaims(null);
    setSubmitErrors([]);
    setActiveClaim(claim);
  }

  function openResubmitModal(claim) {
    if (!isReady(claim) || !canResubmit(claim)) return;
    setBatchClaims(null);
    setSubmitErrors([]);
    setLoadingDetail(claim.id);
    axios
      .post("/api/admin/claims/detail", { patientID: claim.id, period: month })
      .then(({ data }) => {
        setActiveClaim({
          ...claim,
          resubmitForm: data.form,
          acknowledgment: data.acknowledgment,
        });
      })
      .catch(() => {
        setSubmitErrors(["Could not load the previous submission. Please try again."]);
      })
      .finally(() => setLoadingDetail(null));
  }

  function closeClaimModal() {
    if (submitting) return;
    setSubmitErrors([]);
    setActiveClaim(null);
  }

  function openBatchReview() {
    if (selectedIds.size === 0 || !claims) return;
    const selected = claims.filter(
      (c) => selectedIds.has(c.id) && isSelectable(c)
    );
    if (selected.length === 0) return;
    setActiveClaim(null);
    setViewClaim(null);
    setSubmitErrors([]);
    setBatchClaims(selected);
  }

  function closeBatchReview(succeededIds) {
    setBatchClaims(null);
    if (succeededIds?.length) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of succeededIds) next.delete(id);
        return next;
      });
    }
    fetchClaims();
  }

  function submitClaim(claimId, form) {
    setSubmitting(claimId);
    setSubmitErrors([]);
    const payload = {
      ...form,
      patientID: activeClaim.id,
      patientName: `${activeClaim.firstName} ${activeClaim.lastName}`,
      period: month,
    };
    axios
      .post("/api/admin/claims/submit", payload)
      .then(({ data }) => {
        if (data.claimStatus === "submitted") {
          setActiveClaim(null);
          setSelectedIds((prev) => {
            if (!prev.has(claimId)) return prev;
            const next = new Set(prev);
            next.delete(claimId);
            return next;
          });
        } else {
          setSubmitErrors(data.errors || []);
        }
      })
      .catch(() => {
        setSubmitErrors(["Submission failed. Please try again."]);
      })
      .finally(() => {
        fetchClaims();
        setSubmitting(null);
      });
  }

  function viewSubmission(claim) {
    setBatchClaims(null);
    setLoadingDetail(claim.id);
    axios
      .post("/api/admin/claims/detail", { patientID: claim.id, period: month })
      .then(({ data }) => {
        setViewClaim({
          ...claim,
          claimStatus: data.claimStatus,
          controlNumber: data.controlNumber,
          details: data.details,
          form: data.form,
          acknowledgment: data.acknowledgment,
        });
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(null));
  }

  function openPaidModal(claim) {
    if (!canMarkPaid(claim)) return;
    setPaidError(null);
    setPaidClaim(claim);
  }

  function closePaidModal() {
    if (savingPaid) return;
    setPaidError(null);
    setPaidClaim(null);
  }

  function savePaid(date) {
    const patientID = paidClaim.id;
    setSavingPaid(true);
    setPaidError(null);
    axios
      .post("/api/admin/claims/paid", { patientID, period: month, paidAt: date || "" })
      .then(({ data }) => {
        setClaims((prev) =>
          prev?.map((claim) =>
            claim.id === patientID
              ? {
                  ...claim,
                  paid: data.paid,
                  paidAt: data.paidAt,
                  markedPaidByName: data.markedPaidByName,
                }
              : claim
          )
        );
        setPaidClaim(null);
      })
      .catch((e) => {
        setPaidError(e?.response?.data?.error || "Failed to save payment. Please try again.");
      })
      .finally(() => setSavingPaid(false));
  }

  function sortedClaims(list) {
    return [...list].sort((a, b) => a.firstName.localeCompare(b.firstName));
  }

  const periodLabel = claims !== null
    ? DateToFormat(parseDateYMD(month + "-01"), "MF YYYY")
    : "—";

  return (
    <>
      <Helmet title="Claims" />

      {activeClaim && (
        <ClaimFormModal
          claim={activeClaim}
          initialForm={activeClaim.resubmitForm}
          resubmitAcknowledgment={activeClaim.acknowledgment}
          submitting={submitting === activeClaim.id}
          onClose={closeClaimModal}
          onSubmit={(form) => submitClaim(activeClaim.id, form)}
          orgSettings={orgSettings}
          submitErrors={submitErrors}
          leftIcon={<Paperplane height={12} width={12} color="white" />}
        />
      )}

      {viewClaim && (
        <ClaimFormModal
          claim={viewClaim}
          viewMode
          viewForm={viewClaim.form}
          onClose={() => setViewClaim(null)}
          submitting={false}
          onSubmit={() => {}}
          orgSettings={orgSettings}
          submitErrors={[]}
        />
      )}

      {batchClaims && (
        <BatchClaimReviewModal
          claims={batchClaims}
          orgSettings={orgSettings}
          period={month}
          onClose={closeBatchReview}
          onSubmitted={fetchClaims}
        />
      )}

      {paidClaim && (
        <PaidModal
          claim={paidClaim}
          periodLabel={periodLabel}
          submitting={savingPaid}
          error={paidError}
          onClose={closePaidModal}
          onConfirm={savePaid}
          onClear={() => savePaid("")}
        />
      )}

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="typo-hero-small">Claims</div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">

            {/* Month navigator */}
            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="typo-normal-150 text-muted">
                    {claims === null
                      ? "Loading..."
                      : `${claims.length} patient${claims.length === 1 ? "" : "s"}`}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div onClick={() => updatePeriod(-1)} className="item">
                      <ChevronLeft width={16} height={16} color="grey" />
                    </div>
                    <div
                      className="typo-normal-150 text-center ms-2 me-2"
                      style={{ width: 100 }}
                    >
                      {periodLabel}
                    </div>
                    <div onClick={() => updatePeriod(1)} className="item">
                      <ChevronRight width={16} height={16} color="grey" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient list */}
            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    className="typo-normal-150"
                    style={{ color: selectedIds.size > 0 ? "inherit" : "#aaa" }}
                  >
                    {selectedIds.size > 0
                      ? `${selectedIds.size} patient${selectedIds.size === 1 ? "" : "s"} selected`
                      : "Select ready patients to batch submit"}
                  </div>
                  <Button
                    text="Review & Submit"
                    color="blue"
                    size="sm"
                    disabled={selectedIds.size === 0}
                    onClick={openBatchReview}
                  />
                </div>
              </div>

              {/* Header row */}
              <div className="card-header sm">
                <div className="row align-items-center">
                  <div className="col-xs-1 text-center">
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      disabled={selectableIds.length === 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all ready patients"
                      style={{
                        width: 18,
                        height: 18,
                        cursor: selectableIds.length === 0 ? "default" : "pointer",
                      }}
                    />
                  </div>
                  <div className="col-xs-2">
                    <span className="typo-strong-150">Patient</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">G-Code</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">Monthly Check-In</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">Status</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">Paid</span>
                  </div>
                  <div className="col-xs-2">
                    <span className="typo-strong-150">Details</span>
                  </div>
                  <div className="col-xs-3"></div>
                </div>
              </div>

              {/* Loading state */}
              {claims === null && (
                <div className="card-header sm last">
                  <div className="typo-normal-150 text-muted">Loading...</div>
                </div>
              )}

              {/* Rows */}
              {claims !== null && sortedClaims(claims).map((claim, index) => {
                const isLast = index === claims.length - 1;
                const hasSubmitted = hasSubmittedClaim(claim);
                const showResubmit = canResubmit(claim);
                const selectable = isSelectable(claim);
                const isSelected = selectedIds.has(claim.id);
                return (
                  <div
                    key={claim.id}
                    className={"card-header sm " + (isLast ? "last" : "")}
                    style={{ minHeight: 56 }}
                  >
                    <div className="row align-items-center">
                      <div className="col-xs-1 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!selectable}
                          onChange={() => toggleSelected(claim.id)}
                          aria-label={`Select ${claim.firstName} ${claim.lastName}`}
                          style={{
                            width: 18,
                            height: 18,
                            cursor: selectable ? "pointer" : "default",
                          }}
                        />
                      </div>
                      <div className="col-xs-2">
                        <div
                          className="typo-strong-175 link"
                          onClick={() => navigate(`/patient/${claim.id}/card`)}
                        >
                          {claim.firstName} {claim.lastName}
                        </div>
                        {claim.mbi && (
                          <div className="typo-normal-125 text-muted">{claim.mbi}</div>
                        )}
                      </div>
                      <div className="col-xs-1 text-center">
                        {claim.procedureCode ? (
                          <div className="typo-strong-150">{claim.procedureCode}</div>
                        ) : (
                          <span className="typo-normal-150 text-muted">—</span>
                        )}
                      </div>
                      <div className="col-xs-1 text-center">
                        <input
                          type="checkbox"
                          checked={!!claim.monthlyCheckinComplete}
                          readOnly
                          disabled
                          style={{
                            width: 18,
                            height: 18,
                            cursor: "default",
                          }}
                        />
                      </div>
                      <div className="col-xs-1 text-center">
                        {statusBadge(claim)}
                      </div>
                      <div
                        className="col-xs-1 text-center"
                        title={claim.paid ? `Paid · ${claim.paidAt}${claim.markedPaidByName ? ` by ${claim.markedPaidByName}` : ""}` : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={!!claim.paid}
                          disabled={!canMarkPaid(claim)}
                          onChange={() => openPaidModal(claim)}
                          aria-label={`Mark ${claim.firstName} ${claim.lastName} paid`}
                          style={{
                            width: 18,
                            height: 18,
                            cursor: canMarkPaid(claim) ? "pointer" : "default",
                          }}
                        />
                        {claim.paid && claim.paidAt && (
                          <div className="typo-normal-125 text-muted">{claim.paidAt}</div>
                        )}
                      </div>
                      <div className="col-xs-2">
                        {claimDetails(claim) && (
                          <div className="typo-normal-150 mt-1">
                            {claimDetails(claim)}
                          </div>
                        )}
                      </div>
                      <div className="col-xs-3 text-end">
                        {hasSubmitted ? (
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              text={loadingDetail === claim.id ? "Loading..." : "View Submission"}
                              color="green"
                              size="sm"
                              disabled={loadingDetail === claim.id}
                              onClick={() => viewSubmission(claim)}
                            />
                            {showResubmit && (
                              <Button
                                text="Resubmit Claim"
                                color="blue"
                                size="sm"
                                disabled={loadingDetail === claim.id || !isReady(claim)}
                                onClick={() => openResubmitModal(claim)}
                              />
                            )}
                          </div>
                        ) : (
                          <Button
                            text="Submit Claim"
                            color="blue"
                            size="sm"
                            onClick={() => openClaimModal(claim)}
                            disabled={!isReady(claim)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {claims !== null && claims.length === 0 && (
                <div className="card-header sm last">
                  <div className="d-flex align-items-center">
                    <Users height={16} width={16} color="grey" />
                    <span className="typo-normal-150 text-muted ms-2">
                      No claim-eligible patients found
                    </span>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default Claims;
